import { useState, useEffect, useCallback } from 'react';

export const useRealTimeInterests = (supabaseIntegration, userId) => {
  const [userInterests, setUserInterests] = useState(new Set());
  const [userInterestExpirations, setUserInterestExpirations] = useState({});
  const [placeInterestCounts, setPlaceInterestCounts] = useState({});
  const [isConnected, setIsConnected] = useState(false);

  // Function to load user interests
  const loadUserInterests = useCallback(async () => {
    if (!userId || !supabaseIntegration?.supabaseHelpers) return;

    try {
      console.log('📍 Real-time: Loading user interests for user:', userId);
      const { data: interests, error } = await supabaseIntegration.supabaseHelpers.getUserInterests(userId);
      
      if (!error && interests) {
        // Use Google Place IDs (place_id) for frontend compatibility
        const interestedPlaceIds = new Set(interests.map(interest => interest.place_id));
        const expirationTimes = {};
        
        interests.forEach(interest => {
          if (interest.place_id && interest.interest_expires_at) {
            expirationTimes[interest.place_id] = interest.interest_expires_at;
          }
        });
        
        console.log('📍 Real-time: Processing interests data:', {
          rawInterests: interests,
          extractedPlaceIds: Array.from(interestedPlaceIds),
          expirationTimes
        });
        
        console.log('📍 Real-time: User interests updated:', {
          count: interestedPlaceIds.size,
          placeIds: Array.from(interestedPlaceIds)
        });
        
        setUserInterests(interestedPlaceIds);
        setUserInterestExpirations(expirationTimes);
      } else if (error) {
        console.error('📍 Real-time: Error loading user interests:', error);
      }
    } catch (error) {
      console.error('📍 Real-time: Error in loadUserInterests:', error);
    }
  }, [userId, supabaseIntegration]);

  // Function to load place interest counts
  const loadPlaceInterestCounts = useCallback(async (googlePlaceIds) => {
    if (!userId || !supabaseIntegration?.supabaseHelpers || !googlePlaceIds?.length) return;

    try {
      console.log('📍 Real-time: Loading place interest counts for', googlePlaceIds.length, 'places');
      const { data: counts, error } = await supabaseIntegration.supabaseHelpers.getPlaceInterestCounts(userId, googlePlaceIds);
      
      if (!error && counts) {
        const countsWithTimestamp = {
          ...counts,
          _lastRefresh: Date.now()
        };
        setPlaceInterestCounts(countsWithTimestamp);
        console.log('📍 Real-time: Place interest counts updated:', Object.keys(counts).length, 'places');
      } else if (error) {
        console.error('📍 Real-time: Error loading place interest counts:', error);
      }
    } catch (error) {
      console.error('📍 Real-time: Error in loadPlaceInterestCounts:', error);
    }
  }, [userId, supabaseIntegration]);

  useEffect(() => {
    if (!supabaseIntegration?.supabase || !userId) {
      console.log('📍 Real-time interests: Missing supabase or userId');
      return;
    }

    console.log('📍 Setting up real-time interests subscription for user:', userId);
    
    // Initial load
    loadUserInterests();
    
    // Set up real-time subscription for user_place_interests
    const channel = supabaseIntegration.supabase
      .channel('interests-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_place_interests'
        },
        async (payload) => {
          console.log('📍 Real-time interest change:', payload);
          
          // Check if this change affects the current user
          const affectsCurrentUser = payload.new?.user_id === userId || payload.old?.user_id === userId;
          
          if (affectsCurrentUser) {
            console.log('📍 Real-time: Change affects current user, reloading interests');
            // Reload user's interests when their interests change
            await loadUserInterests();
          }
          
          // Always reload place counts as other users' interests affect counts
          // We'll trigger this through a callback to the component using this hook
          console.log('📍 Real-time: Interest change detected, place counts may need refresh');
        }
      )
      .subscribe((status) => {
        console.log('📍 Real-time interests subscription status:', status);
        setIsConnected(status === 'SUBSCRIBED');
        
        if (status === 'CHANNEL_ERROR') {
          console.error('📍 Real-time interests subscription error');
        } else if (status === 'TIMED_OUT') {
          console.warn('📍 Real-time interests subscription timed out');
        } else if (status === 'CLOSED') {
          console.log('📍 Real-time interests subscription closed');
        }
      });
    
    return () => {
      console.log('📍 Cleaning up real-time interests subscription');
      supabaseIntegration.supabase.removeChannel(channel);
      setIsConnected(false);
    };
  }, [supabaseIntegration, userId, loadUserInterests]);

  return {
    userInterests,
    userInterestExpirations,
    placeInterestCounts,
    setPlaceInterestCounts, // For manual updates
    loadPlaceInterestCounts, // For triggering place counts refresh
    isConnected,
    refresh: loadUserInterests // For manual refresh
  };
}; 